import { useCallback, useContext, useEffect, useMemo, useState, type Key, type MouseEvent } from "react";
import { UpOutlined, DownOutlined, PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Dropdown, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import clsx from "clsx";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { journalsQueries, TitlePageFillersDropdown } from "@/entities/journal";
import {
  JournalSectionContent,
  JournalSectionDescription,
  JournalSectionFilters,
  JournalSectionPageHeader,
  JournalSectionPagination,
  JournalSectionRecordsToolbar,
  JournalSectionWrapper,
  useJournalSectionPagination,
  JournalRecordPreview,
  type JournalRecordPreviewMeta,
  isTablePreviewClick,
} from "@/entities/journal/ui";
import { JournalSectionNavTitle, useJournalSectionPersonnelPageConfig } from "@/features/journal-section-personnel";
import {
  AbilityContext,
  Can,
  EntityBaseAbilityBuilder,
  useUpdateAbility,
} from "@/shared/lib/casl";
import { useFlags } from "@/shared/lib/flags";
import { useDebounce } from "@/shared/utils";
import {
  useCoordinator,
  useLocalUser,
  useNavigationPosition,
} from "@shared/lib/hooks";
import { useAppRoutes } from "@shared/lib/react-router";
import { JournalBulkSendToSignModal } from "../../../journal-bulk-send-to-sign";
import { RejectedRecordsCollapsible } from "../../../journals-section-first/ui";
import { editButtonMenu } from "../../model";
import { useSectionThreeController } from "../../model/hooks/use-section-three-controller";
import { getColumns } from "../../model/table-columns";
import { getData } from "../../model/table-data";
import { useSetupSigningString } from "../../model/utils/setup-signing-string";
import { useWithSignedString } from "../../model/utils/with-signed-string";
import { useSectionThreeActions } from "../journal-section-form/model/hooks/use-section-three-actions";
import {
  SectionThirdActiveFilterChips,
  SectionThirdFilters,
} from "../journal-section-form/ui/section-third-filters/section-third-filters";
import { JournalVersionModal } from "./journal-version-modal";
import { RejectModal } from "./ui/reject-modal";
import "./journals-section-third.scss";

export const JournalsSectionThird = () => {
  const localUser = useLocalUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get("status");

  const { mainJournalId, titleId, projectId, objectId } = useCoordinator();
  const { isInJournalVersionSection } = useNavigationPosition();
  const appRoutes = useAppRoutes();
  const isVersionsEnabled = useFlags("ojrFormationFlowEnabledV1") === true;

  const isFormOrViewPage = location.pathname.includes("/form/") || location.pathname.includes("/view/");
  const isPersonnelPage = location.pathname.includes("/personnel");
  const { recordsTitle, recordsPath } = useJournalSectionPersonnelPageConfig(3);

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const { data: titleJournal, isLoading: isTitleLoading } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );
  const signersList = useMemo(
    () => [
      ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor?.representatives ?? []),
      ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor?.constructionControls ?? []),
    ],
    [titleJournal],
  );

  const [search, setSearch] = useState("");
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    statusFromUrl ? statusFromUrl.split(",").filter(Boolean) : [],
  );
  const [workTypes, setWorkTypes] = useState<string[]>([]);
  const [signerIds, setSignerIds] = useState<string[]>([]);
  const [actConnect, setActConnect] = useState(false);
  const [createDate, setCreateDate] = useState("");
  const [updateDate, setUpdateDate] = useState("");

  const debouncedSearch = useDebounce(search, 300);
  const [open, setOpen] = useState(false);

  const {
    signingString,
    setupSigningString,
    signingItemId,
  } = useSetupSigningString(mainJournalData, debouncedSearch);
  const { withSignedString } = useWithSignedString();

  const [isBulkSendModalOpen, setIsBulkSendModalOpen] = useState(false);
  const [, setSelectedRowKeys] = useState<Key[]>([]);
  const [previewRecord, setPreviewRecord] = useState<JournalRecordPreviewMeta | null>(
    null,
  );
  const { deleteSectionEntry } = useSectionThreeActions(mainJournalData);

  const {
    handleReturn,
    handleStatusReturn,
    handleSendToSign,
    handleSendToSignBatch,
    handleReject,
  } = useSectionThreeController(
    mainJournalData?.mainJournal?.section3?.sectionListId || "",
    mainJournalData?.mainJournal?.section3?.sectionId || "",
  );

  const pagination = useJournalSectionPagination(10);
  const { page, setPage, limit } = pagination;

  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [versionsModalItemNoVersionId, setVersionsModalItemNoVersionId] = useState<string | null>(null);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectItemNoVersionId, setRejectItemNoVersionId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");

  const { data: sectionData, isFetching } = useQuery({
    ...journalsQueries.getSection3({
      sectionThreeListId:
        mainJournalData?.mainJournal?.section3?.sectionListId || "",
      search: debouncedSearch,
      page,
      limit,
      status: selectedStatuses.join(","),
      dateFrom: dateStart,
      dateTo: dateEnd,
      workType: workTypes.join(","),
      actConnect: actConnect ? "true" : "",
      signerId: signerIds.join(","),
      createDate,
      updateDate,
    }),
    enabled: !!mainJournalData?.mainJournal?.section3?.sectionListId,
  });

  const rejectedWithComment = (sectionData?.section3List || []).filter(
    (item: any) => {
      if (item?.status !== "REJECTED") return false;
      if (item?.comments && Array.isArray(item.comments) && item.comments.length > 0) {
        const validComments = item.comments.filter(
          (comment: any) => comment?.comment && comment.comment.trim(),
        );
        return validComments.length > 0;
      }
      return (item?.comment ?? "").toString().trim();
    },
  );

  useEffect(() => {
    const id = searchParams.get("itemNoVersionId");
    const act = searchParams.get("action");
    
    if (id && act === "EDIT") {
      navigate(
        appRoutes.journals.section3Form(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      setSearchParams(params);
    } else if (id && act === "VIEW") {
      navigate(
        appRoutes.journals.section3View(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      setSearchParams(params);
    } else if (!id && act === "ADD") {
      const workTypeParam = searchParams.get("workType");
      const url = appRoutes.journals.section3Form(projectId, objectId, mainJournalId, titleId, "new");
      const params = new URLSearchParams();
      if (workTypeParam) {
        params.set("workType", workTypeParam);
      }
      const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
      navigate(fullUrl);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("action");
      setSearchParams(newParams);
    }
  }, [searchParams, navigate, projectId, objectId, mainJournalId, titleId, setSearchParams]);

  useUpdateAbility({
    // @ts-expect-error я тебя ненавижу
    permissions: sectionData?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder(),
  });
  const ability = useContext(AbilityContext);


  const handleOpenForm = ({
    isEdit = false,
    itemNoVersionId: formItemNoVersionId,
    workType,
  }: {
    initTitle?: string;
    workType?: string;
    isEdit?: boolean;
    itemNoVersionId?: string;
  }) => {
    if (signersList.length > 0) {
      if (isEdit && formItemNoVersionId) {
        // Navigate to form page for editing
        navigate(
          appRoutes.journals.section3Form(projectId, objectId, mainJournalId, titleId, formItemNoVersionId),
        );
      } else {
        // Navigate to form page for new entry with workType in query params
        const url = appRoutes.journals.section3Form(projectId, objectId, mainJournalId, titleId, "new");
        const params = new URLSearchParams();
        if (workType) {
          params.set("workType", workType);
        }
        const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
        navigate(fullUrl);
      }
      }
  };

  const handleOpenFormInfo = (itemNoVersionId: string) => {
    // Navigate to view page
    navigate(
      appRoutes.journals.section3View(projectId, objectId, mainJournalId, titleId, itemNoVersionId),
    );
  };

  const data = getData(sectionData);

  const menu = editButtonMenu({
    handleOpenForm: (initTitle, workType) =>
      handleOpenForm({ initTitle, workType, isEdit: false }),
  });

  const handleAction = async (action: string, actionPayload?: any) => {
    switch (action) {
      case "SEND":
        await handleSendToSign({
          itemNoVersionId: actionPayload?.itemNoVersionId,
        });
        break;
      case "REJECT":
        setRejectItemNoVersionId(actionPayload?.itemNoVersionId || null);
        setRejectComment("");
        setRejectCommentError("");
        setRejectModalOpen(true);
        break;
      case "DELETE":
        await deleteSectionEntry({
          params: {
            path: { itemNoVersionId: actionPayload?.itemNoVersionId },
          },
        });
        break;
      case "RETURN":
        await handleReturn({
          itemNoVersionId: actionPayload?.itemNoVersionId,
        });
        break;
      case "SEND_MULTIPLE":
        await handleSendToSignBatch(actionPayload);
        setSelectedRowKeys([]);
        break;
      case "RECALL":
        await handleStatusReturn({
          itemNoVersionId: actionPayload?.itemNoVersionId,
        });
        break;
      default:
        break;
    }
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setRejectItemNoVersionId(null);
    setRejectComment("");
    setRejectCommentError("");
  };

  const handleRejectComment = (comment: string) => {
    setRejectComment(comment);
  };

  const handleRejectSubmit = async (setError: (error: string) => void) => {
    if (!rejectComment.trim()) {
      setError("Укажите причину отклонения");
      return;
    }
    if (rejectItemNoVersionId) {
      await handleReject({
        itemNoVersionId: rejectItemNoVersionId,
        comment: rejectComment,
        signerEmployeeId: localUser?.user_id || "",
      });
    }
    handleCloseRejectModal();
  };

  const handleStartBulkSign = () => {
    setIsBulkSendModalOpen(true);
  };

  const isAddDisabled = signersList.length === 0;
  const sectionThreeId =
    mainJournalData?.mainJournal?.section3?.sectionListId ||
    mainJournalData?.mainJournal?.section3?.sectionId;

  const columns = getColumns({
    handleAction,
    handleOpenFormInfo,
    handleOpenForm,
    onOpenVersionsModal: isVersionsEnabled
      ? (itemNoVersionId: string) => {
          setVersionsModalItemNoVersionId(itemNoVersionId);
          setVersionsModalOpen(true);
        }
      : undefined,
    isVersionsEnabled,
    localUser,
    mainJournalData,
    debouncedSearch,
    signingItemId,
    withSignedString,
    setupSigningString,
    signingString,
  });

  const resolveBulkSendTable = useCallback(
    (data: unknown) => ({
      rowKey: "key",
      tableClassName: "journals-section-third-table",
      dataSource: getData(data as Parameters<typeof getData>[0]),
      columns: getColumns({
        bulkSendMode: true,
        handleAction: async () => {},
        handleOpenFormInfo: () => {},
        handleOpenForm: () => {},
        isVersionsEnabled: false,
        localUser,
        mainJournalData,
        debouncedSearch,
        signingItemId: null,
        withSignedString: async () => {},
        setupSigningString: async () => {},
        signingString: "",
      }) as ColumnsType,
    }),
    [localUser, mainJournalData, debouncedSearch],
  );

  return (
    <>
      {isFormOrViewPage || isPersonnelPage ? (
        <Outlet />
      ) : (
        <>
      <JournalSectionWrapper>
        {!isInJournalVersionSection && !isTitleLoading ? (
          <JournalSectionPageHeader
            banner={
              isAddDisabled ? (
                <>
                  Для добавления записи в Раздел 3 не хватает информации в
                  Титульном листе в блоке{" "}
                  <span className="journal-section-page-header__hint-link">
                    Уполномоченный представитель лица, осуществляющего
                    строительство
                  </span>
                  .{" "}
                  <TitlePageFillersDropdown
                    sectionType="sectionThree"
                    sectionId={sectionThreeId}
                  />
                </>
              ) : undefined
            }
            title={
              <JournalSectionNavTitle
                recordsTitle={recordsTitle}
                recordsPath={recordsPath}
                activeTab="records"
              />
            }
            actions={
              <Can I="edit" a="section_3">
                <JournalSectionRecordsToolbar
                  onStartBulkSign={handleStartBulkSign}
                  showBulkActions={!isAddDisabled}
                  addButton={
                    <Dropdown
                      overlay={menu}
                      onOpenChange={(flag) => setOpen(flag)}
                      open={open}
                      trigger={["click"]}
                      disabled={isAddDisabled}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        disabled={isAddDisabled}
                        style={{
                          backgroundColor: isAddDisabled
                            ? "rgba(119, 119, 119, 1)"
                            : undefined,
                          borderColor: isAddDisabled
                            ? "rgba(119, 119, 119, 1)"
                            : undefined,
                        }}
                      >
                        Добавить запись {open ? <UpOutlined /> : <DownOutlined />}
                      </Button>
                    </Dropdown>
                  }
                />
              </Can>
            }
          />
        ) : (
          <JournalSectionNavTitle
            recordsTitle={recordsTitle}
            recordsPath={recordsPath}
            activeTab="records"
          />
        )}
        <JournalSectionDescription>
          <span className="journals-section-third-description">
            Сведения о выполнении работ в процессе строительства, реконструкции,
            капитального ремонта объекта капитального строительства.
            <Tooltip
              title={
                <>
                  Раздел доступен сотрудникам организаций: застройщик,
                  эксплуатирующая организации, региональный оператор, технический
                  заказчик, лицо осуществляющее подготовку проектной документации,
                  авторский надзор, лицо осуществляющее строительство.
                  <br />
                  <br />
                  Сотрудники иных организаций имеют доступ только к записям
                  созданные их сотрудниками.
                </>
              }
            >
              <InfoCircleOutlined className="journals-section-third-description__info" />
            </Tooltip>
          </span>
        </JournalSectionDescription>

        <JournalSectionFilters>
          <SectionThirdFilters
            onSearchChange={setSearch}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            workTypes={workTypes}
            setWorkTypes={setWorkTypes}
            signerIds={signerIds}
            setSignerIds={setSignerIds}
            actConnect={actConnect}
            setActConnect={setActConnect}
            createDate={createDate}
            setCreateDate={setCreateDate}
            updateDate={updateDate}
            setUpdateDate={setUpdateDate}
            dateStart={dateStart}
            dateEnd={dateEnd}
            setDateStart={setDateStart}
            setDateEnd={setDateEnd}
            signersList={signersList}
            onFiltersApply={() => setPage(1)}
          />
        </JournalSectionFilters>

        <JournalSectionContent>
          {rejectedWithComment.length > 0 ? (
            <div className="journal-info-block--spaced-bottom">
              <RejectedRecordsCollapsible
                items={rejectedWithComment}
                editSubject="section_3"
                onReturnToWork={async (item) => {
                  await handleAction("RETURN", { itemNoVersionId: (item as any)?.itemNoVersionId });
                }}
              />
            </div>
          ) : null}
          <AbilityContext.Provider value={ability}>
            <SectionThirdActiveFilterChips
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={setSelectedStatuses}
              workTypes={workTypes}
              setWorkTypes={setWorkTypes}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              actConnect={actConnect}
              setActConnect={setActConnect}
              dateStart={dateStart}
              dateEnd={dateEnd}
              setDateStart={setDateStart}
              setDateEnd={setDateEnd}
              signersList={signersList}
              onFilterRemove={() => setPage(1)}
            />
            <Table
              className="journals-section-third-table"
              rowKey="key"
              rowClassName={(record: any) =>
                clsx(
                  "journals-section-third-row",
                  record?.info?.status === "REJECTED" && "section-row--rejected",
                  previewRecord?.itemNoVersionId === record?.key &&
                    "journal-section-row--preview-selected",
                )
              }
              onRow={(record: any) => ({
                onClick: (event: MouseEvent<HTMLElement>) => {
                  if (!isTablePreviewClick(event)) return;
                  setPreviewRecord({
                    itemNoVersionId: record.key,
                    sequenceNumber: record.number,
                    status: record.info?.status ?? record.status,
                    isCurrent: record.info?.isCurrent,
                  });
                },
              })}
              // @ts-expect-error типы колонок таблицы не совпадают с ColumnsType
              columns={columns}
              dataSource={data}
              pagination={false}
              loading={isFetching}
            />
            <JournalSectionPagination
              pagination={pagination}
              total={sectionData?.total || 0}
            />
          </AbilityContext.Provider>
        </JournalSectionContent>
      </JournalSectionWrapper>

          <JournalBulkSendToSignModal
            open={isBulkSendModalOpen}
            onClose={() => setIsBulkSendModalOpen(false)}
            section={3}
            sectionThreeListId={
              mainJournalData?.mainJournal?.section3?.sectionListId
            }
            signersList={signersList}
            resolveTable={resolveBulkSendTable}
            onSendBatch={async (items) => {
              await handleSendToSignBatch(items);
              setIsBulkSendModalOpen(false);
            }}
          />

          {isVersionsEnabled ? (
            <JournalVersionModal
              open={versionsModalOpen}
              onCancel={() => {
                setVersionsModalOpen(false);
                setVersionsModalItemNoVersionId(null);
              }}
              sectionThreeListId={mainJournalData?.mainJournal?.section3?.sectionListId || ""}
              itemNoVersionId={versionsModalItemNoVersionId || undefined}
              sequenceNumber={
                sectionData?.section3List?.find(
                  (item: any) =>
                    item?.itemNoVersionId === versionsModalItemNoVersionId,
                )?.sequenceNumber
              }
            />
          ) : null}

          <RejectModal
            open={rejectModalOpen}
            rejectComment={rejectComment}
            rejectCommentError={rejectCommentError}
            sectionThreeListId={mainJournalData?.mainJournal?.section3?.sectionListId || ""}
            debouncedSearch={debouncedSearch}
            page={page}
            limit={limit}
            selectedStatuses={selectedStatuses}
            dateStart={dateStart}
            dateEnd={dateEnd}
            workTypeFilterId={workTypes.join(",")}
            idFilterId={actConnect ? "true" : ""}
            signerFilterId={signerIds.join(",")}
            createDate={createDate}
            updateDate={updateDate}
            onCancel={handleCloseRejectModal}
            onCommentChange={handleRejectComment}
            onReject={handleRejectSubmit}
          />
          <JournalRecordPreview
            sectionNumber={3}
            record={previewRecord}
            onClose={() => setPreviewRecord(null)}
            onOpenRecord={(itemNoVersionId) => {
              navigate(
                appRoutes.journals.section3View(
                  projectId,
                  objectId,
                  mainJournalId,
                  titleId,
                  itemNoVersionId,
                ),
              );
              setPreviewRecord(null);
            }}
          />
        </>
      )}
    </>
  );
};
